import { readonly, ref } from "vue";

// The navigator lives in the legacy bundle, which is the only place that can reach a menu item and
// the click machinery behind it. Islands get to it the same way they get to pdfMake — through the
// window, because neither bundle can import the other.
interface PayrollNavigatorGlobal {
  go(target: string, action?: string | null): boolean;
  can_reach(target: string): boolean;
  take_action(target: string): string | null;
}

declare global {
  interface Window {
    payroll_navigator?: PayrollNavigatorGlobal;
  }
}

export const NAVIGATOR_READY_EVENT = "merit:navigator-ready";

// The legacy bundle builds the main menu inside jQuery's ready handler, which runs *after* this
// bundle — a module script — has mounted its islands and rendered them once. Asking about a
// destination before then asks a document that has no menu in it yet, and the answer would stand
// forever. Reactive so the one caller that renders on the answer gets to change its mind.
const ready = ref(Boolean(window.payroll_navigator));

if (!ready.value) {
  window.addEventListener(NAVIGATOR_READY_EVENT, () => { ready.value = true; }, { once: true });
}

export const navigatorReady = readonly(ready);

/**
 * Whether the destination exists on this page and the user may go there. False also covers the
 * legacy bundle not being loaded at all, which is the case in tests and on login-side pages.
 */
export function canReach(target: string | null): boolean {
  if (!navigatorReady.value || !target || !window.payroll_navigator) return false;

  return window.payroll_navigator.can_reach(target);
}

export function navigateTo(target: string, action?: string | null): void {
  window.payroll_navigator?.go(target, action);
}

/**
 * What the destination was asked to do on arrival, or null. One-shot: reading it clears it, so
 * coming back to the same view later does not re-apply something the user has since undone.
 */
export function takeArrivalAction(target: string): string | null {
  return window.payroll_navigator?.take_action(target) ?? null;
}
